import {
  all,
  call,
  delay,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { IAppState } from 'spw-redux/reducer';
import { isEmpty, isNil, merge, uniq } from 'ramda';
import _, { isNumber, snakeCase } from 'lodash';
import moment from 'moment';
import {
  createChildWorksheetSpaceType as createChildWorksheetSpaceTypeApi,
  createTotalProgram as createTotalProgramApi,
  createTotalProgramNote as createTotalProgramNoteApi,
  createWorksheetNote as createWorksheetNoteApi,
  fetchAllChildWorksheetSpaces as fetchAllChildWorksheetSpacesApi,
  postWorksheetRow as postWorksheetRowApi,
  putWorksheet,
  saveEditingBuilding,
  Scenario as scenarioApi,
  TotalProgram as totalProgramApi,
  updateBuildingFloor,
  updateChildWorksheetSpaceType as updateChildWorksheetSpaceTypeApi,
  Worksheet as worksheetApi,
} from 'spw-api';
import { removeTypeNameFromObject, replaceAtIndex } from 'spw-shared/utils';
import {
  DEFAULT_SHARED_SPACES_FIELDS_TO_KEEP,
  GROWTH_CALCULATORS,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  REQUEST_STATUS,
  TOTAL_PROGRAM_TYPE,
  WORKSHEET_VALUES,
} from 'spw-constants';
import {
  getRowRatio,
  isDepartmentScenario,
  isFloorScenario,
  isHCSCScenario,
  isMacro,
  isStorageSupportSpace,
  uniqueRow,
  updateWorksheetRows,
} from 'spw-components/worksheet/utils';
import {
  handleError,
  showToastMessage,
} from 'spw-redux/modules/UI/messages/actions';
import {
  ID_TYPE,
  setSummarySelectedIdType,
} from 'spw-redux/modules/program-summary/actions';
import {
  GET_DEPARTMENTS_BY_PROJECT_ID_SUCCEEDED,
  getDepartmentsByProjectId,
  getDepartmentsByProjectIdSucceeded,
  updateNumberOfScenarios,
  updateProjectBuildingsSucceeded,
} from 'spw-redux/modules/projects/actions';
import {
  getTotalProgramLinkedScenarios,
  generateLinkedScenariosIdsMap,
  sortScenarios,
} from 'spw-components/programming-scenarios/utils';
import { selectProjectSettings } from 'spw-redux/modules/settings/selector';
import {
  IProjectSpaceType,
  ISpaceType,
  ISpaceTypeCategory,
} from 'spw-redux/modules/space_types/types';
import {
  actions as spaceTypeActions,
  FETCH_PROJECT_SPACE_TYPES_SUCCESS,
} from 'spw-redux/modules/space_types/actions';
import { getDefaultSpaceTypeCategory } from 'spw-redux/modules/project-space-type-categories/utils';
import { APIResponse, IPayloadAction } from 'spw-redux/types';
import { isTotalProgram } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import * as scenarioActions from 'spw-redux/modules/calculators/actions';
import {
  actions,
  BUILD_NEW_SCENARIO,
  BULK_UPDATE_SCENARIO,
  BULK_UPDATE_TOTAL_PROGRAM,
  CLONE_SCENARIO,
  CREATE_TOTAL_PROGRAM,
  CREATE_TOTAL_PROGRAM_NOTE,
  CREATE_WORKSHEET_NOTE,
  createChildWorksheetSpaceSuccess,
  createTotalProgramSuccess,
  DELETE_SCENARIO,
  FETCH_ALL_CHILD_WORKSHEET_SPACES,
  FETCH_ALL_CHILD_WORKSHEET_SPACES_SUCCESS,
  FETCH_TPS_WORKSHEETS,
  FETCH_CURRENT_WORKSHEET,
  fetchTpsWorksheets,
  fetchAllChildWorksheetSpaces as fetchAllChildWorksheetSpacesAction,
  fetchAllChildWorksheetSpacesSuccess,
  fetchTpsWorksheetsSuccess,
  GET_PROJECT_SCENARIOS,
  GET_SCENARIO,
  GET_TOTAL_PROGRAM,
  getScenariosSuccess,
  POPULATE_WORKSHEET_FROM_REQUEST,
  populateWorksheet,
  populateWorksheetFromRequest as populateWorksheetFromRequestAction,
  POST_WORKSHEET_ROW,
  PREPARE_SCENARIO_FOR_UPDATE,
  REBUILD_TOTAL_PROGRAM_SCENARIO_VIEW,
  SAVE_SCENARIO,
  SAVE_UPDATING_SCENARIO,
  SAVE_WORKSHEET,
  saveUpdatingScenario as saveUpdatingScenarioAction,
  saveWorksheet as saveWorksheetAction,
  SET_PLANNING_FACTOR,
  SET_SUBTOTAL_BUILDING_MULTIPLIER,
  SET_SUBTOTAL_PROGRAM_MULTIPLIER,
  SET_RSF_LOAD_FACTOR,
  SET_TOTAL_PROGRAM_AVAILABLE_USF,
  setCurrentScenario,
  setIsEditingSavedScenario,
  setLoadingScenarios,
  totalProgramScenariosFetchSuccess,
  UPDATE_CHILD_WORKSHEET_SPACE_TYPE,
  UPDATE_DEPARTMENT_SCENARIOS,
  UPDATE_LINKED_SCENARIOS,
  UPDATE_SCENARIO,
  UPDATE_TOTAL_PROGRAM,
  updateBuildingScenarios,
  updateLinkedScenarios,
  updateDepartmentScenarios as updateDepartmentScenariosAction,
  fetchProjectWorksheetsSuccess,
  FETCH_PROJECT_WORKSHEETS,
  buildTotalPrograms,
  BUILD_TOTAL_PROGRAMS,
  getScenariosFailed,
  fetchProjectWorksheetsFailed,
  totalProgramScenariosFetchFailed,
  fetchProjectWorksheets,
  FETCH_PROJECT_WORKSHEETS_SUCCESS,
  fetchCurrentWorksheetFailed,
  fetchCurrentWorksheetSuccess,
  getProjectScenarios as getProjectScenariosAction,
  fetchCurrentWorksheet as fetchCurrentWorksheetAction,
} from 'spw-redux/modules/calculators/actions';

import {
  isCustomSubcategory,
  isIWCategoryOrSubcategory,
} from 'spw-projects/utils';
import {
  ApiScenario,
  BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS,
  CalculatorType,
  ICategoryNote,
  IChildWorksheetSpaceType,
  isWorksheetAPIObject,
  ITotalProgram,
  ITotalProgramScenario,
  IWorksheet,
  IWorksheetRow,
  IWorksheetRowValue,
  Scenario,
  YEARS_AHEAD,
} from './types';
import {
  IBuilding,
  IDepartment,
  IProject,
  IProjectSpaceTypeCategory,
} from '../projects/types';
import {
  selectAllChildWorksheetSpaces,
  selectCurrentScenario,
  selectTemporaryScenario,
  selectTpsWorksheets,
  selectTotalPrograms,
  selectWorksheet,
  selectWorksheets,
  selectScenarios,
} from './selectors';
import {
  currentProject as currentProjectSelector,
  selectProjectDepartments,
  projectSpaceTypes as projectSpaceTypesSelector,
  selectProjectSpaceTypeCategories,
} from '../projects/selectors';
import { hydrateWorksheetRows } from './utils';
import ProgramScenario, {
  PlainProgramScenario,
} from '../../../models/Scenario';
import Worksheet, { PlainWorksheet } from '../../../models/Worksheet';
import ProgramScenarioFactory from '../../../models/ProgramScenarioFactory';
import TotalProgramWorksheet from '../../../models/TotalProgramWorksheet';
import TotalProgramScenario from '../../../models/TotalProgramScenario';
import { IProjectSettings } from '../settings/types';
import { selectIsFetchingSpaceTypes } from '../space_types/selectors';
import WorksheetRow from '../../../models/WorksheetRow';
import WorksheetStorageSpaceRow from '../../../models/WorksheetStorageSpaceRow';

const prepScenarioForUpdate = (
  currentScenario: Scenario,
  action?: ReturnType<
    | typeof actions.setSubtotalBuildingMultiplier
    | typeof actions.setSubtotalProgramMultiplier
    | typeof actions.setRsfLoadFactor
  >
): Partial<ApiScenario> => {
  const s = {
    ...currentScenario,
    vacancyFactor: parseFloat(currentScenario.vacancyFactor as string),
    headcountUtilization: parseFloat(
      currentScenario.headcountUtilization as string
    ),
    deskSharingRatio: parseFloat(currentScenario.deskSharingRatio as string),
    subtotalBuildingMultiplier:
      action?.type === SET_SUBTOTAL_BUILDING_MULTIPLIER
        ? action?.payload.value
        : currentScenario.subtotalBuildingMultiplier,
    subtotalProgramMultiplier:
      action?.type === SET_SUBTOTAL_PROGRAM_MULTIPLIER
        ? action?.payload.value
        : currentScenario.subtotalProgramMultiplier,
    rsfLoadFactor:
      action?.type === SET_RSF_LOAD_FACTOR
        ? action?.payload.value
        : currentScenario.rsfLoadFactor,
    hcscGrowthRate: !isNil(currentScenario.headcountSeatCountGrowthRate)
      ? currentScenario.headcountSeatCountGrowthRate
      : null,
    hcscBase: currentScenario.headcountSeatCountBase,
    departmentId: currentScenario.department?.id,
  };

  return _.omit(s, [
    'worksheet',
    'building',
    'headcountSeatCountBase',
    'headcountSeatCountGrowthRate',
    'department',
  ]);
};

/**
 * @param {IWorksheet} worksheet
 * @param {ICategoryNote} note
 * @returns {Notes[]} Updated worksheet notes
 */
function getUpdatedWorksheetNotes(
  worksheet: IWorksheet | PlainWorksheet,
  note: ICategoryNote
) {
  const notes = worksheet?.notes?.slice() || [];

  const oldNoteIndex = _.findIndex(
    notes,
    (n: ICategoryNote) => n.category === note.category
  );

  if (oldNoteIndex > -1) {
    notes.splice(oldNoteIndex, 1, note);
  } else {
    notes.push(note);
  }

  return notes;
}

function* buildNewScenario(
  action: ReturnType<typeof actions.buildNewScenario>
) {
  try {
    const calculatorType: null | CalculatorType = yield select(
      (state: IAppState) => state.calculatorReducer.currentCalculatorType
    );
    const project: IProject = yield select(currentProjectSelector);
    const currentScenario: PlainProgramScenario = yield select(
      selectCurrentScenario
    );

    if (calculatorType === null) {
      if (currentScenario !== null) {
        yield put(actions.buildNewScenarioSuccess(null));
      }
      return;
    }

    const startYear =
      calculatorType ===
      GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments
        ? project.year
        : moment().year();

    // default floor id is the first floor with USF if is floor scenario. If none of the floors have USF, then just take the first floor
    const floorId =
      calculatorType === 'floor-headcount-seatcount' ||
      calculatorType === 'floor-usf'
        ? (
            project.buildings[0]?.floors?.find((f) => f.totalFloorUsf) ??
            project.buildings[0]?.floors[0]
          )?.id
        : null;

    // default scenario values
    const scenario: Partial<Scenario> = {
      calculatorType,
      targetUsf: 0,
      vacancyFactor: '0.0',
      headcountUtilization: '100',
      deskSharingRatio: '1.00',
      growthYears: YEARS_AHEAD,
      buildingId: project.buildings[0]?.id,
      building: project.buildings[0],
      projectId: project.id,
      floorId,
      department: null,
      base: calculatorType === GROWTH_CALCULATORS.macroProgramming ? 0 : null,
      headcountSeatCountGrowthRate: 0,
      headcountSeatCountBase: 0,
      ...(action.payload.options?.shouldMergeWithCurrentScenario
        ? {
            ...ProgramScenario.toAPIScenario(currentScenario),
            targetYear:
              startYear +
              currentScenario.targetYear -
              currentScenario.startYear,
          }
        : {
            targetYear: startYear,
          }),
      startYear,
      endYear: startYear + YEARS_AHEAD,
    };
    // start building growth table

    if (calculatorType === BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS) {
      let departments: IDepartment[] = yield select(
        (state: IAppState) => state.projectsReducer.departments
      );
      if (isEmpty(departments)) {
        yield put(getDepartmentsByProjectId(project.id));
        const deptAction: ReturnType<typeof getDepartmentsByProjectIdSucceeded> = yield take(
          GET_DEPARTMENTS_BY_PROJECT_ID_SUCCEEDED
        );
        departments = deptAction.payload.departments;
      }
      if (!action.payload.options?.shouldMergeWithCurrentScenario) {
        scenario.department = _.head(departments)
          ? (_.head(departments) as IDepartment)
          : null;
      }
    }

    yield put(actions.buildNewScenarioSuccess(scenario));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateScenario(
  action: ReturnType<typeof scenarioActions.updateScenario>
) {
  try {
    const { scenario, changes } = action.payload;

    if (isTotalProgram(scenario)) {
      const totalProgram = TotalProgramScenario.toAPITotalProgram(scenario);
      const updatedTotalProgram = ProgramScenarioFactory.create(
        merge(totalProgram, {
          name: changes?.name ?? totalProgram.name,
        }),
        scenario.linkedScenarios
      );
      yield put(setCurrentScenario(updatedTotalProgram));
    } else {
      const apiScenario = ProgramScenario.toAPIScenario(scenario);

      const updatedScenario: Scenario = merge(apiScenario, {
        name: changes?.name ? changes.name : scenario.name,
        note: changes?.note ? changes.note : scenario.note,
        startYear: changes?.startYear ? changes.startYear : scenario.startYear,
        endYear: changes?.startYear
          ? changes.startYear + YEARS_AHEAD
          : scenario.startYear + YEARS_AHEAD,
        targetYear: changes?.targetYear
          ? changes.targetYear
          : scenario.targetYear,
        floorId: changes?.floorId ? changes?.floorId : scenario?.floorId,
        department: changes?.department
          ? changes.department
          : scenario.department,
        targetUsf:
          changes?.targetUsf !== null && changes.targetUsf !== undefined
            ? changes?.targetUsf
            : scenario?.targetUSF,
        deskSharingRatio: changes?.deskSharingRatio
          ? (changes?.deskSharingRatio as number)
          : scenario?.deskSharingRatio,
        headcountUtilization: changes?.headcountUtilization
          ? changes?.headcountUtilization
          : scenario?.headcountUtilizationPercentage,
        vacancyFactor: changes?.vacancyFactor
          ? changes?.vacancyFactor
          : scenario?.vacancyFactorPercentage,
        usf: changes?.usf ? changes?.usf : scenario?.totalAvailableUSF,
        rsfLoadFactor:
          changes?.rsfLoadFactor !== undefined
            ? changes?.rsfLoadFactor
            : _.round(scenario?.rsfLoadFactorPercentage / 100, 4),
      });

      yield put(actions.buildNewScenarioSuccess(updatedScenario, true));
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* prepareScenarioForUpdate() {
  try {
    const temporaryScenario: PlainProgramScenario = yield select(
      selectTemporaryScenario
    );

    const currentScenario: PlainProgramScenario = yield select(
      selectCurrentScenario
    );
    const worksheet: PlainWorksheet = yield select(selectWorksheet);
    const scenarioBeforeUpdate: PlainProgramScenario = temporaryScenario;
    const isThereAtLeastOneRowWithQuantity = !!worksheet.rows.find(
      (r) => !r.removed && r.quantity > 0
    );
    const areStoredWorkSeatsOutdated =
      scenarioBeforeUpdate.workSeats !== currentScenario.workSeats &&
      isThereAtLeastOneRowWithQuantity;
    const shouldSaveWithoutWorksheetUpdateDialog = !areStoredWorkSeatsOutdated;
    if (shouldSaveWithoutWorksheetUpdateDialog) {
      yield put(setIsEditingSavedScenario(false));

      yield put(
        saveUpdatingScenarioAction(
          false,
          false,
          DEFAULT_SHARED_SPACES_FIELDS_TO_KEEP,
          WORKSHEET_VALUES.TARGET_RATIO as IWorksheetRowValue
        )
      );
    }
    yield put(
      setCurrentScenario({
        ...currentScenario,
        areStoredWorkSeatsOutdated,
        storedWorkSeats: areStoredWorkSeatsOutdated
          ? scenarioBeforeUpdate.workSeats
          : null,
      })
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* saveScenario(
  action: ReturnType<typeof scenarioActions.saveScenario>
) {
  try {
    const {
      scenarioName,
      scenarioNote,
      floorId,
      isDetailed,
      isShared,
      departmentId,
      redirect,
    } = action.payload;

    let currentScenario: PlainProgramScenario = yield select(
      selectCurrentScenario
    );
    const tempScenario: PlainProgramScenario = yield select(
      selectTemporaryScenario
    );
    const project: IProject = yield select(currentProjectSelector);
    const building = project.buildings[0];

    const tempScenarioWorkSeats = tempScenario ? tempScenario.workSeats : 0;

    const worksheet: PlainWorksheet = yield select(selectWorksheet);

    const isSaveAsMethodBeingUsed = !!tempScenario;

    const scenarioToSave = {
      ..._.omit(ProgramScenario.toAPIScenario(currentScenario), [
        'building',
        'headcountSeatCountBase',
        'headcountSeatCountGrowthRate',
        'department',
      ]),
      name: scenarioName,
      note: scenarioNote,
      hcscGrowthRate:
        !isNil(currentScenario.headcountSeatCountGrowthRate) &&
        isHCSCScenario(currentScenario.calculatorType)
          ? currentScenario.headcountSeatCountGrowthRate
          : null,
      hcscBase: currentScenario.headcountSeatCountBase,
      projectId: project.id,
      departmentId: currentScenario.department?.id,
      buildingId: currentScenario.building.id,
      isDetailed,
      isShared,
    };

    const haveWorkSeatsChanged =
      tempScenarioWorkSeats !== currentScenario.workSeats;
    if (
      isSaveAsMethodBeingUsed &&
      haveWorkSeatsChanged &&
      worksheet.hasAtLeastOneRowWithQuantity
    ) {
      scenarioToSave.areStoredWorkSeatsOutdated = true;
      scenarioToSave.storedWorkSeats = tempScenarioWorkSeats;
    }

    // When the scenario is cloned the id of the scenario to save is deleted
    delete scenarioToSave.id;

    if (floorId) {
      scenarioToSave.floorId = floorId;
    }
    if (departmentId) {
      scenarioToSave.departmentId = departmentId;
    }

    const {
      data: saveScenarioResponse,
    }: APIResponse<typeof scenarioApi.saveNewScenario> = yield call(
      scenarioApi.saveNewScenario,
      removeTypeNameFromObject(_.omitBy(scenarioToSave, _.isNil))
    );

    const { scenario } = saveScenarioResponse;

    scenario.calculatorType = scenario.calculatorType.replace(
      /_/g,
      '-'
    ) as CalculatorType;
    const scenarioAfterSaving = { ...scenario, building };

    yield put(
      setCurrentScenario(ProgramScenarioFactory.create(scenarioAfterSaving))
    );
    yield put(populateWorksheetFromRequestAction(scenario.worksheet));

    let bldgOrFloor = ID_TYPE.buildingId;
    let { id } = scenario;
    if (scenario?.floorId) {
      if (building) {
        const floor = _.find(building.floors, { id: scenario.floorId });
        if (floor) {
          bldgOrFloor = ID_TYPE.floorId;
          id = floor.id;
        }
      }
    } else if (building) {
      ({ id } = building);
    }

    if (isMacro(scenario)) {
      bldgOrFloor = ID_TYPE.macro;
    }

    if (
      scenario?.calculatorType ===
        GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments ||
      scenario?.calculatorType === TOTAL_PROGRAM_TYPE
    ) {
      bldgOrFloor = ID_TYPE.departments;
    }
    // set the sidebar appropriately
    yield put(setSummarySelectedIdType(id, bldgOrFloor));
    yield put(
      showToastMessage({
        status: 'success',
        text: 'The scenario was successfully saved!',
      })
    );
    currentScenario = yield select(selectCurrentScenario);
    const oldScenarios: PlainProgramScenario[] = yield select(selectScenarios);
    const worksheets: PlainWorksheet[] = yield select(selectWorksheets);
    const currentWorksheet: PlainWorksheet = yield select(selectWorksheet);

    const totalPrograms: PlainProgramScenario[] = yield select(
      selectTotalPrograms
    );

    const scenarios = oldScenarios.slice();
    scenarios.push(currentScenario);

    // update building scenarios
    yield put(updateBuildingScenarios(scenarios));
    yield put(fetchProjectWorksheetsSuccess([currentWorksheet, ...worksheets]));
    yield put(
      updateNumberOfScenarios(
        (scenarios?.length || 0) + (totalPrograms?.length || 0)
      )
    );
    yield put(scenarioActions.saveScenarioSuccess());

    if (redirect) {
      redirect(currentScenario);
    }
  } catch (e) {
    yield put(scenarioActions.saveScenarioFailed());
    yield put(handleError(e));
  }
}

function* cloneScenario(action: ReturnType<typeof actions.cloneScenario>) {
  try {
    const {
      scenarioId,
      name,
      isDetailed,
      isShared,
      shouldCopyNotes,
      optionsForCopy,
      optionsForCopyWithDifferentCalculator,
      redirect,
    } = action.payload;

    const worksheet: PlainWorksheet = yield select(selectWorksheet);
    const temporaryScenario: PlainProgramScenario = yield select(
      selectTemporaryScenario
    );
    const currentScenario: PlainProgramScenario = yield select(
      selectCurrentScenario
    );
    const currentProject: IProject = yield select(currentProjectSelector);
    let newAPIScenario: Scenario;
    if (optionsForCopyWithDifferentCalculator) {
      const {
        calculatorType,
        department,
        floor,
      } = optionsForCopyWithDifferentCalculator;
      const newScenario = ProgramScenarioFactory.parseToAnotherCalculatorType(
        currentScenario,
        calculatorType,
        { department, floorId: floor?.id, departmentsYear: currentProject.year }
      );
      newAPIScenario = ProgramScenario.toAPIScenario(newScenario);
    } else {
      newAPIScenario = ProgramScenario.toAPIScenario(currentScenario);
    }

    const temporaryScenarioWorkSeats = temporaryScenario
      ? temporaryScenario.workSeats
      : currentScenario.workSeats;

    const scenarioChanges: Partial<ApiScenario> = {
      ..._.omit(newAPIScenario, [
        'id',
        'projectId',
        'building',
        'department',
        'headcountSeatCountBase',
        'headcountSeatCountGrowthRate',
      ]),
      name,
      isDetailed,
      isShared,
    };

    const scenarioIsHCSCBased = isHCSCScenario(scenarioChanges.calculatorType);
    if (scenarioIsHCSCBased) {
      scenarioChanges.hcscBase = newAPIScenario.headcountSeatCountBase;
      scenarioChanges.hcscGrowthRate = !isNil(
        newAPIScenario.headcountSeatCountGrowthRate
      )
        ? newAPIScenario.headcountSeatCountGrowthRate
        : null;
    }
    const scenarioIsFloorBased = isFloorScenario(
      scenarioChanges.calculatorType
    );
    if (optionsForCopy?.scenarioFloorId && scenarioIsFloorBased) {
      scenarioChanges.floorId = optionsForCopy?.scenarioFloorId;
    }
    if (
      optionsForCopy?.scenarioDepartmentId &&
      scenarioChanges.calculatorType ===
        GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments
    ) {
      scenarioChanges.departmentId = optionsForCopy?.scenarioDepartmentId;
    }

    const haveWorkSeatsChanged =
      temporaryScenarioWorkSeats !== currentScenario.workSeats;

    if (haveWorkSeatsChanged && worksheet.hasAtLeastOneRowWithQuantity) {
      scenarioChanges.areStoredWorkSeatsOutdated = true;
      scenarioChanges.storedWorkSeats = temporaryScenarioWorkSeats;
    }
    const { selectedBuildingUSF, selectedFloorUSF, floor } =
      optionsForCopyWithDifferentCalculator || {};
    const shouldAlsoUpdateFloor =
      !!selectedFloorUSF && floor && !floor.totalFloorUsf;
    const shouldAlsoUpdateBuilding = !!selectedBuildingUSF;
    if (shouldAlsoUpdateFloor) {
      const buildingResponse: APIResponse<typeof updateBuildingFloor> = yield call(
        updateBuildingFloor,
        {
          ...floor,
          totalFloorUsf: selectedFloorUSF,
        },
        true
      );
      const { updatedBuilding } = buildingResponse.data;
      yield put(updateProjectBuildingsSucceeded([updatedBuilding]));
    }
    if (shouldAlsoUpdateBuilding) {
      const buildingResponse: APIResponse<typeof saveEditingBuilding> = yield call(
        saveEditingBuilding,
        {
          ...currentScenario.building,
          programmingUsf: selectedBuildingUSF,
          projectId: currentScenario.projectId,
          id: currentScenario.building.id,
        }
      );
      const { buildings } = buildingResponse.data;
      yield put(updateProjectBuildingsSucceeded(buildings));
    }
    scenarioChanges.calculatorType = snakeCase(
      scenarioChanges.calculatorType
    ) as CalculatorType;

    const {
      data: response,
    }: APIResponse<typeof scenarioApi.cloneScenario> = yield call(
      scenarioApi.cloneScenario,
      scenarioId,
      scenarioChanges as ApiScenario,
      shouldCopyNotes
    );

    let { scenario } = response;

    scenario = {
      ...scenario,
      calculatorType: scenario.calculatorType.replace(
        /_/g,
        '-'
      ) as CalculatorType,
    };

    yield put(fetchAllChildWorksheetSpacesAction());
    yield put(getProjectScenariosAction());
    yield put(setCurrentScenario(scenario));

    if (redirect) {
      redirect(scenario);
    }

    yield put(
      showToastMessage({
        status: 'success',
        text: 'The scenario was successfully saved!',
      })
    );
  } catch (error) {
    yield put(handleError(error));
  }
}

function* getProjectScenarios(
  action: ReturnType<typeof actions.getProjectScenarios>
) {
  try {
    let { projectId } = action.payload;
    const currentProject: IProject = yield select(currentProjectSelector);
    if (!projectId) projectId = currentProject?.id;
    const {
      data: scenariosResponse,
    }: APIResponse<typeof scenarioApi.getProjectScenarios> = yield call(
      scenarioApi.getProjectScenarios,
      projectId
    );
    const { scenarios: scenarioResponseVals } = scenariosResponse;

    // load and hydrate scenarios
    const scenarios: PlainProgramScenario[] = scenarioResponseVals.map(
      (scenario) => {
        const buildingScenario: Scenario = {
          ...scenario,
          vacancyFactor: scenario.vacancyFactor?.toString(),
          headcountUtilization: scenario.headcountUtilization?.toString(),
          deskSharingRatio: scenario.deskSharingRatio?.toString(),
        };

        return ProgramScenarioFactory.create(buildingScenario).toPlainObject();
      }
    );

    // TODO: Move the child worksheet spaces to the project space types table
    yield put(actions.fetchAllChildWorksheetSpaces());
    yield take(FETCH_ALL_CHILD_WORKSHEET_SPACES_SUCCESS);

    const { data: totalProgramScenarios } = yield call(
      totalProgramApi.fetchTotalPrograms,
      projectId
    );
    const totalPrograms: PlainProgramScenario[] = [];
    _.forEach(totalProgramScenarios.totalPrograms, (t: ITotalProgram) => {
      const linkedScenariosMap = getTotalProgramLinkedScenarios(t);

      const linkedScenarios = scenarios.filter((_scenario) => {
        return linkedScenariosMap[_scenario.id];
      });

      const totalProgram = {
        ...t,
        calculatorType: TOTAL_PROGRAM_TYPE as 'total-program',
      };
      totalPrograms.push(
        ProgramScenarioFactory.create(
          totalProgram,
          linkedScenarios
        ).toPlainObject()
      );
    });

    yield put(
      updateNumberOfScenarios(
        (scenarios?.length || 0) + (totalPrograms?.length || 0)
      )
    );
    yield put(getScenariosSuccess(scenarios));
    yield put(totalProgramScenariosFetchSuccess(totalPrograms));
    yield put(setLoadingScenarios(false));
  } catch (error) {
    yield put(getScenariosFailed());
    yield put(totalProgramScenariosFetchFailed());
    yield put(handleError(error));
  }
}

function* getScenario(
  action: IPayloadAction<'GET_SCENARIO', { scenarioId: number }>
) {
  try {
    const { scenarioId } = action.payload;
    const { data } = yield call(scenarioApi.getScenario, scenarioId);

    if (data.scenario) {
      const { scenario } = data;
      yield put(setCurrentScenario(scenario));
    }
  } catch (error) {
    yield put(handleError(error));
  }
}

function* getTotalProgram(action: ReturnType<typeof actions.getTotalProgram>) {
  try {
    const { totalProgramId } = action.payload;
    const {
      data,
    }: APIResponse<typeof totalProgramApi.getTotalProgram> = yield call(
      totalProgramApi.getTotalProgram,
      totalProgramId
    );

    if (data.totalProgram) {
      const { totalProgram } = data;
      const associatedScenarios: Scenario[] = [];
      totalProgram.totalProgramScenario = totalProgram.totalProgramScenario.map(
        (linkedScenario: ITotalProgramScenario) => {
          const { scenario } = linkedScenario;
          associatedScenarios.push(scenario);
          return {
            ...linkedScenario,
            scenario,
          };
        }
      );

      yield put(
        setCurrentScenario(
          {
            ...totalProgram,
            calculatorType: TOTAL_PROGRAM_TYPE as 'total-program',
          },
          associatedScenarios
        )
      );
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* buildTotalProgramsCall() {
  const totalPrograms: PlainProgramScenario[] = yield select(
    selectTotalPrograms
  );
  const scenarios: PlainProgramScenario[] = yield select(selectScenarios);
  const updatedTotalPrograms: PlainProgramScenario[] = [];
  _.forEach(totalPrograms, (t) => {
    const linkedScenariosMap = generateLinkedScenariosIdsMap(t);
    const linkedScenarios = scenarios.filter((_scenario) => {
      return linkedScenariosMap[_scenario.id];
    });
    updatedTotalPrograms.push(
      ProgramScenarioFactory.create(
        TotalProgramScenario.toAPITotalProgram(t),
        linkedScenarios
      ).toPlainObject()
    );
  });
  yield put(totalProgramScenariosFetchSuccess(updatedTotalPrograms));
}

function* saveScenarioWorkSeatsRoundingSettingCall(
  action: ReturnType<
    typeof scenarioActions.saveScenarioWorkSeatsRoundingSetting
  >
) {
  try {
    const currentScenario: PlainProgramScenario = yield select(
      selectCurrentScenario
    );

    const newScenario: Scenario = {
      ...ProgramScenario.toAPIScenario(currentScenario),
      workSeatsRoundingSetting: action.payload,
    };

    yield call(scenarioApi.updateScenario, prepScenarioForUpdate(newScenario));
    const updatedScenario = ProgramScenarioFactory.create(
      newScenario
    ).toPlainObject();

    const projectScenarios: PlainProgramScenario[] = yield select(
      selectScenarios
    );
    const updatedScenarioIndex = projectScenarios.findIndex(
      (s) => s.id === currentScenario.id
    );
    const newProjectScenarios = replaceAtIndex(
      projectScenarios,
      updatedScenario,
      updatedScenarioIndex
    );
    yield put(getScenariosSuccess(newProjectScenarios));

    yield put(setCurrentScenario(updatedScenario));
    yield put(fetchCurrentWorksheetAction());
  } catch (error) {
    yield put(handleError(error));
  }
}

function* saveUpdatingScenario(
  action: ReturnType<
    | typeof actions.saveUpdatingScenario
    | typeof actions.setSubtotalProgramMultiplier
    | typeof actions.setRsfLoadFactor
    | typeof actions.setSubtotalBuildingMultiplier
    | typeof actions.setPlanningFactor
  >
) {
  try {
    let currentScenario: PlainProgramScenario = yield select(
      selectCurrentScenario
    );
    let scenario: ITotalProgram | Scenario;

    if (
      action.type === SET_RSF_LOAD_FACTOR &&
      (currentScenario.calculatorType === TOTAL_PROGRAM_TYPE ||
        isDepartmentScenario(currentScenario))
    ) {
      yield put(updateDepartmentScenariosAction(action.payload.value));
    } else if (currentScenario.calculatorType === TOTAL_PROGRAM_TYPE) {
      const {
        data: { totalProgram },
      }: APIResponse<typeof totalProgramApi.updateTotalProgram> = yield call(
        totalProgramApi.updateTotalProgram,
        _.pick(TotalProgramScenario.toAPITotalProgram(currentScenario), [
          'id',
          'name',
          'index',
          'subtotalProgramMultiplier',
          'planningFactor',
          'usf',
          'usfType',
        ])
      );

      scenario = {
        ...totalProgram,
        calculatorType: TOTAL_PROGRAM_TYPE,
      };

      if (
        action.type === SET_SUBTOTAL_PROGRAM_MULTIPLIER ||
        action.type === SET_PLANNING_FACTOR
      ) {
        yield put(
          updateLinkedScenarios(
            scenario as ITotalProgram,
            action.type === SET_SUBTOTAL_PROGRAM_MULTIPLIER
              ? // The scenario subtotalBuildingMultiplier will be updated
                // with the subtotalProgramMultiplier value of the total program
                { subtotalBuildingMultiplier: action.payload.value }
              : // The scenario subtotalProgramMultiplier value will be updated
                // with the planningFactor value of the total program
                { subtotalProgramMultiplier: action.payload.value }
          )
        );
      }
    } else {
      // reduce size and remove unnecessary props from objs
      const newScenario: Scenario = {
        ...ProgramScenario.toAPIScenario(currentScenario),
        areStoredWorkSeatsOutdated: false,
        storedWorkSeats: null,
      };

      const { data: updateScenarioResponse } = yield call(
        scenarioApi.updateScenario,
        prepScenarioForUpdate(newScenario)
      );

      ({ scenario } = updateScenarioResponse);
      scenario.calculatorType = scenario.calculatorType.replace(
        /_/g,
        '-'
      ) as CalculatorType;

      yield put(setCurrentScenario(ProgramScenarioFactory.create(newScenario)));
      currentScenario = yield select(selectCurrentScenario);
      const scenarios: PlainProgramScenario[] = yield select(selectScenarios);
      const scenariosUpdated = scenarios.map((s) =>
        s.id === currentScenario.id ? currentScenario : s
      );
      yield put(getScenariosSuccess(scenariosUpdated));
      yield put(buildTotalPrograms());

      const worksheet: PlainWorksheet = yield select(selectWorksheet);

      if (worksheet && action.type === SAVE_UPDATING_SCENARIO) {
        const projectSettings: IProjectSettings = yield select(
          selectProjectSettings
        );
        currentScenario = yield select(selectCurrentScenario);
        const totalWorkSeats = currentScenario.workSeats;
        // update all rows based on total workseats
        const apiWorksheet = Worksheet.toAPIWorksheet(worksheet);

        const rows = updateWorksheetRows(
          apiWorksheet,
          totalWorkSeats,
          action.payload.sharedSpacesFieldsToKeep,
          action.payload.workspacesFieldToKeep,
          undefined,
          currentScenario.areStoredWorkSeatsOutdated
            ? currentScenario.storedWorkSeats
            : undefined
        );
        const updatedAPIWorksheet: IWorksheet = { ...apiWorksheet, rows };
        const updatedWorksheet = new Worksheet(
          updatedAPIWorksheet,
          currentScenario,
          projectSettings
        );
        yield put(populateWorksheet(updatedWorksheet));
        yield put(saveWorksheetAction());
        if (action.payload.onSucess) {
          action.payload.onSucess();
        }
      }
    }

    if (action.type === SAVE_UPDATING_SCENARIO) {
      if (!action.payload.hideUpdate) {
        yield put(
          showToastMessage({
            status: 'success',
            text: 'The scenario was successfully saved!',
          })
        );
      }

      if (action.payload.refresh) {
        if (isTotalProgram(scenario as Scenario)) {
          const tpsScenariosEnhanced: PlainProgramScenario[] = yield select(
            selectTotalPrograms
          );
          const updatedTpsScenariosEnhanced = tpsScenariosEnhanced.map((tpse) =>
            tpse.id === scenario.id
              ? { ...tpse, ...(scenario as ITotalProgram) }
              : tpse
          );
          yield put(
            totalProgramScenariosFetchSuccess(updatedTpsScenariosEnhanced)
          );
        }
      }
    }
  } catch (error) {
    yield put(handleError(error));
  }
}

// TODO: Add new saga to update worksheet only
function* saveWorksheet() {
  try {
    const worksheet: PlainWorksheet = yield select(selectWorksheet);
    if (worksheet) {
      const isPreExistingWorksheet = worksheet.id && worksheet.id !== -1;
      let i = 0;
      const apiWorksheet = Worksheet.toAPIWorksheet(worksheet);
      const postValue = _({
        ...apiWorksheet,
        notes: isPreExistingWorksheet
          ? apiWorksheet.notes?.map((note: ICategoryNote) =>
              removeTypeNameFromObject(note)
            )
          : apiWorksheet.notes?.map((note: ICategoryNote) =>
              _.omit(note, ['id'])
            ),
        worksheetRows: apiWorksheet?.rows?.length
          ? _(apiWorksheet.rows)
              // filter out removed worksheet rows
              .filter((r) => !r.removed)
              .uniqBy(uniqueRow)
              .uniqBy((r: IWorksheetRow) => {
                return `${isPreExistingWorksheet ? r.id || i++ : i++}`;
              })
              .map((r) => {
                // remove unnecessary props from obj sent to database
                return _.omit(
                  {
                    ...r,
                    projectSpaceTypeId: !r.child
                      ? r?.projectSpaceType?.id
                      : null,
                    spaceId: r.spaceId || r.projectSpaceType.spaceType.id,
                  },
                  [
                    'spaceType',
                    'projectSpaceType',
                    'category',
                    'presorted',
                    'removed',
                    'subcategory',
                  ]
                );
              })
              .value()
          : [],
      })
        .omitBy(_.isNil)
        .omit(['rows'])
        .value();
      let newWorksheet;
      if (isPreExistingWorksheet) {
        const response: APIResponse<typeof putWorksheet> = yield call(
          putWorksheet,
          postValue
        );
        ({ putWorksheet: newWorksheet } = response.data);
      }
      yield put(populateWorksheetFromRequestAction(newWorksheet));
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* fetchProjectWorksheetsCall(
  action: ReturnType<typeof actions.fetchProjectWorksheets>
) {
  try {
    let { scenarioIds } = action.payload;
    const scenarios: PlainProgramScenario[] = yield select(selectScenarios);
    if (!scenarioIds || !scenarios.length) {
      scenarioIds = scenarios.map((s) => s.id);
    }

    const {
      data: { getWorksheets: data },
    }: APIResponse<typeof worksheetApi.fetchWorksheets> = yield call(
      worksheetApi.fetchWorksheets,
      scenarioIds
    );

    const projectSpaceTypesCategories: IProjectSpaceTypeCategory[] = yield select(
      selectProjectSpaceTypeCategories
    );

    if (data.length && scenarios?.length) {
      const projectSettings: IProjectSettings = yield select(
        selectProjectSettings
      );
      const childWorksheetSpaces: IChildWorksheetSpaceType[] = yield select(
        selectAllChildWorksheetSpaces
      );
      const projectSpaceTypes: IProjectSpaceType[] = yield select(
        projectSpaceTypesSelector
      );
      const apiWorksheets: IWorksheet[] = _.map(data, (worksheet) => {
        const rows = worksheet.worksheetRows || worksheet.rows;

        return {
          ...worksheet,
          rows: hydrateWorksheetRows({
            projectSpaceTypes,
            rows,
            childWorksheetSpaces,
            projectSpaceTypesCategories,
          }),
        };
      });

      const worksheets = apiWorksheets.map((ws) =>
        new Worksheet(
          ws,
          scenarios.find((s) => s.id === ws.scenarioId),
          projectSettings
        ).toPlainObject()
      );
      yield put(fetchProjectWorksheetsSuccess(worksheets));
    }
  } catch (e) {
    yield put(fetchProjectWorksheetsFailed());
    yield put(handleError(e));
  }
}

function* fetchTpsWorksheetsCall() {
  try {
    const currentScenario: PlainProgramScenario = yield select(
      selectCurrentScenario
    );
    if (!currentScenario) return;
    const project: IProject = yield select(currentProjectSelector);
    const scenarios: PlainProgramScenario[] = yield select(selectScenarios);
    let departments: IDepartment[] = yield select(selectProjectDepartments);

    const scenariosMap: Record<number, PlainProgramScenario> = scenarios.reduce(
      (map, scenario) => {
        return {
          ...map,
          [scenario.id]: scenario,
        };
      },
      {}
    );

    if (isEmpty(departments)) {
      yield put(getDepartmentsByProjectId(project.id));
      const getDepartmentsSucceededAction: ReturnType<typeof getDepartmentsByProjectIdSucceeded> = yield take(
        GET_DEPARTMENTS_BY_PROJECT_ID_SUCCEEDED
      );
      departments = getDepartmentsSucceededAction.payload.departments;
    }

    const scenarioIds = currentScenario.linkedScenarios.map((s) => s.id);
    const {
      data: { getWorksheets: data },
    }: APIResponse<typeof worksheetApi.fetchWorksheets> = yield call(
      worksheetApi.fetchWorksheets,
      scenarioIds
    );
    const projectSpaceTypesCategories: IProjectSpaceTypeCategory[] = yield select(
      selectProjectSpaceTypeCategories
    );

    if (data.length && scenarios?.length) {
      const projectSettings: IProjectSettings = yield select(
        selectProjectSettings
      );
      const childWorksheetSpaces: IChildWorksheetSpaceType[] = yield select(
        selectAllChildWorksheetSpaces
      );
      const projectSpaceTypes: IProjectSpaceType[] = yield select(
        projectSpaceTypesSelector
      );
      const apiWorksheets: IWorksheet[] = _.map(data, (worksheet) => {
        const rows = worksheet.worksheetRows || worksheet.rows;

        return {
          ...worksheet,
          rows: hydrateWorksheetRows({
            projectSpaceTypes,
            rows: rows.map((row: IWorksheetRow) => ({
              ...row,
              department:
                scenariosMap[worksheet.scenarioId as number].department,
            })),
            childWorksheetSpaces,
            projectSpaceTypesCategories,
          }),
        };
      });

      const worksheets = apiWorksheets.map((ws) =>
        new Worksheet(
          ws,
          scenarios.find((s) => s.id === ws.scenarioId),
          projectSettings
        ).toPlainObject()
      );
      yield put(fetchTpsWorksheetsSuccess(worksheets));
      yield put(actions.rebuildTotalProgramScenarioView());
    }

    yield put(fetchCurrentWorksheetSuccess());
  } catch (e) {
    yield put(fetchCurrentWorksheetFailed());
    yield put(handleError(e));
  }
}

function* fetchCurrentWorksheet() {
  try {
    const currentScenario: PlainProgramScenario = yield select(
      selectCurrentScenario
    );

    if (!currentScenario?.id) return;

    const { id, calculatorType } = currentScenario;

    if (calculatorType === GROWTH_CALCULATORS.totalProgram) {
      yield put(fetchTpsWorksheets());
      return;
    }

    const response: APIResponse<typeof worksheetApi.fetchWorksheet> = yield call(
      worksheetApi.fetchWorksheet,
      id
    );
    const { worksheet } = response.data;

    if (worksheet && worksheet.worksheetRows) {
      yield put(populateWorksheetFromRequestAction(worksheet));
    }
    yield put(fetchCurrentWorksheetSuccess());
  } catch (e) {
    yield put(fetchCurrentWorksheetFailed());
    yield put(handleError(e));
  }
}

function* deleteScenario(action: ReturnType<typeof actions.deleteScenario>) {
  try {
    const { redirect } = action.payload;
    const currentScenario: PlainProgramScenario = yield select(
      selectCurrentScenario
    );

    // this logic involves redirecting to appropriate view after deleting
    if (currentScenario.calculatorType === TOTAL_PROGRAM_TYPE) {
      const { data: response } = yield call(
        totalProgramApi.deleteTotalProgram,
        currentScenario.id
      );
      const totalPrograms: PlainProgramScenario[] = yield select(
        selectTotalPrograms
      );
      yield put(
        totalProgramScenariosFetchSuccess(
          totalPrograms.filter((tps) => tps.id !== currentScenario.id)
        )
      );

      const deletedTotalProgram = response.deleteTotalProgram;

      if (redirect) {
        redirect({
          ...deletedTotalProgram,
          calculatorType: TOTAL_PROGRAM_TYPE,
        } as ITotalProgram);
      }
      yield put(setCurrentScenario(null));
    } else {
      const { data: response } = yield call(
        scenarioApi.deleteScenario,
        currentScenario.id
      );

      const deletedScenario: Partial<Scenario> = response.deleteScenario;

      if (redirect) {
        redirect(deletedScenario as Scenario);
      }

      yield put(setCurrentScenario(null));
    }

    const currentScenarios: PlainProgramScenario[] = yield select(
      selectScenarios
    );
    const totalPrograms: PlainProgramScenario[] = yield select(
      selectTotalPrograms
    );

    const scenarios = currentScenarios.slice();
    const remainingScenarios = scenarios.filter(
      (s) => s.id !== currentScenario?.id
    );
    const remainingTotalPrograms = totalPrograms.filter(
      (t) => t.id !== currentScenario?.id
    );
    const totalProgramsWithoutDeletedScenarioLinked = totalPrograms.map(
      (t) => ({
        ...t,
        linkedScenarios: t.linkedScenarios.filter(
          (s) => s.id !== currentScenario.id
        ),
      })
    );
    yield put(
      totalProgramScenariosFetchSuccess(
        totalProgramsWithoutDeletedScenarioLinked
      )
    );
    yield put(updateBuildingScenarios(remainingScenarios));
    yield put(
      updateNumberOfScenarios(
        (remainingScenarios?.length || 0) +
          (remainingTotalPrograms?.length || 0)
      )
    );
    yield put(
      showToastMessage({
        status: 'success',
        text: 'The scenario was successfully deleted!',
      })
    );
  } catch (error) {
    yield put(handleError(error));
  }
}

function* postWorksheetRow(
  action: ReturnType<typeof actions.postWorksheetRow>
) {
  try {
    const { row, worksheetId, isNew, unitsHigh, name } = action.payload;

    const projectSpaceTypes: IProjectSpaceType[] = yield select(
      projectSpaceTypesSelector
    );
    let childWorksheetSpaceType: IChildWorksheetSpaceType;

    if (row.child) {
      const childWorksheetSpaces: IChildWorksheetSpaceType[] = yield select(
        selectAllChildWorksheetSpaces
      );

      childWorksheetSpaceType = _.find(
        childWorksheetSpaces,
        (c) => c?.id === row?.spaceType?.id
      );

      if (!childWorksheetSpaceType || isNew) {
        // Child worksheet space does not exist for new row, so it needs to be created
        const currentProject: IProject = yield select(currentProjectSelector);
        const response: APIResponse<typeof createChildWorksheetSpaceTypeApi> = yield call(
          createChildWorksheetSpaceTypeApi,
          {
            name,
            spaceTypeId: row.spaceType.id || row.spaceTypeId,
            projectId: currentProject.id,
            unitsHigh,
          }
        );

        ({ childWorksheetSpaceType } = response.data);
      }
    }

    // now send new worksheet row with the correct space/spaceTypeId
    const response: APIResponse<typeof postWorksheetRowApi> = yield call(
      postWorksheetRowApi,
      _.omit(
        {
          ...removeTypeNameFromObject(row),
          spaceId: childWorksheetSpaceType
            ? childWorksheetSpaceType.id
            : row.spaceType.id,
          projectSpaceTypeId: row?.projectSpaceType?.id,
        },
        ['spaceType', 'projectSpaceType', 'removed', 'category', 'subcategory']
      ),
      worksheetId
    );

    let parentSpace: ISpaceType;
    let parentProjectSpaceType: IProjectSpaceType;
    let newChildSpace: IChildWorksheetSpaceType;

    // correctly assign parent to child worksheet row
    if (childWorksheetSpaceType) {
      parentProjectSpaceType = projectSpaceTypes.find(
        (projectSpaceType: IProjectSpaceType) => {
          return (
            projectSpaceType.spaceType.id ===
              childWorksheetSpaceType?.parent?.id ||
            projectSpaceType.spaceType.id ===
              childWorksheetSpaceType?.spaceTypeId
          );
        }
      );

      const categories: ISpaceTypeCategory[] = yield select(
        (state: IAppState) => state.projectsReducer.spaceTypeCategories
      );

      // assign parent category appropriately
      parentSpace = {
        ...parentProjectSpaceType?.spaceType,
        spaceTypeCategory:
          !isCustomSubcategory(parentProjectSpaceType.category) &&
          isIWCategoryOrSubcategory(parentProjectSpaceType.category)
            ? categories.find(
                (category: ISpaceTypeCategory) =>
                  category.category === INDIVIDUAL_WORKSPACES_CATEGORY
              )
            : (parentProjectSpaceType?.category
                .defaultSpaceTypeCategory as ISpaceTypeCategory),
      };

      newChildSpace = {
        ...childWorksheetSpaceType,
        spaceType: parentSpace?.spaceType,
        parent: parentSpace,
      };

      yield put(createChildWorksheetSpaceSuccess(newChildSpace));
    }

    const newRowData = response?.data?.worksheetRow;

    const newProjectSpaceType = {
      ...newRowData.projectSpaceType,
      category: row.child
        ? parentProjectSpaceType.category
        : {
            ...newRowData.projectSpaceType.category,
            defaultSpaceTypeCategory: getDefaultSpaceTypeCategory(
              newRowData.projectSpaceType.category.defaultSpaceTypeCategory
            ),
          },
    };

    if (!newRowData?.child) {
      if (
        !projectSpaceTypes.find(
          (projectSpaceType: IProjectSpaceType) =>
            projectSpaceType.id === newProjectSpaceType.id
        )
      ) {
        yield put(spaceTypeActions.fetchProjectSpaceTypes());
        yield take(FETCH_PROJECT_SPACE_TYPES_SUCCESS);
      }
    }

    yield put(scenarioActions.fetchCurrentWorksheet());
  } catch (e) {
    yield put(handleError(e));
  }
}

function* createWorksheetNote(
  action: ReturnType<typeof actions.createWorksheetNote>
) {
  try {
    const { note, worksheetId } = action.payload;

    yield call(
      createWorksheetNoteApi,
      removeTypeNameFromObject(note),
      worksheetId
    );
    const worksheet: PlainWorksheet = yield select(selectWorksheet);
    if (worksheetId === worksheet?.id) {
      const notes = getUpdatedWorksheetNotes(worksheet, note);
      const updatedWorksheet = {
        ...worksheet,
        notes,
      };

      yield put(populateWorksheet(updatedWorksheet));
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* createTotalProgramNote(
  action: ReturnType<typeof actions.createTotalProgramNote>
) {
  try {
    const { note, totalProgramId } = action.payload;

    yield call(
      createTotalProgramNoteApi,
      removeTypeNameFromObject(note),
      totalProgramId
    );
    const worksheet: PlainWorksheet = yield select(selectWorksheet);
    const currentScenario: PlainProgramScenario = yield select(
      selectCurrentScenario
    );

    const notes = getUpdatedWorksheetNotes(worksheet, note);
    const updatedWorksheet = {
      ...worksheet,
      notes,
    };
    yield put(populateWorksheet(updatedWorksheet));

    const scenarios: PlainProgramScenario[] = yield select(selectScenarios);
    const linkedScenarios = generateLinkedScenariosIdsMap(currentScenario);
    const associatedScenarios = scenarios.filter(
      (sc) => linkedScenarios[sc.id]
    );
    const updatedScenario = {
      ...currentScenario,
      notes,
    };
    yield put(setCurrentScenario(updatedScenario, associatedScenarios));
    const tpsScenariosEnhanced: PlainProgramScenario[] = yield select(
      selectTotalPrograms
    );

    const updatedTpsScenariosEnhanced = tpsScenariosEnhanced.map((tps) =>
      tps.id === updatedScenario.id ? { ...tps, notes } : tps
    );
    yield put(totalProgramScenariosFetchSuccess(updatedTpsScenariosEnhanced));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* bulkUpdateScenarios(
  action: ReturnType<typeof actions.bulkUpdateScenarios>
): Generator<any, PlainProgramScenario[] | void, any> {
  try {
    const { scenarios } = action.payload;

    const scenariosById = _.reduce<Scenario, { [key: number]: Scenario }>(
      scenarios,
      (scenariosAcc, s) => ({ ...scenariosAcc, [s.id]: s }),
      {}
    );

    // only update scenarios that are different
    if (scenarios && scenarios.length) {
      const currentScenarios: PlainProgramScenario[] = yield select(
        selectScenarios
      );
      const existingScenarios = scenarios.filter((s) => s.id);
      const updates = call(
        scenarioApi.bulkUpdateScenarios,
        existingScenarios.map((s) => prepScenarioForUpdate(s))
      );

      yield updates;

      const buildings = _.reduce<Scenario, { [key: number]: IBuilding }>(
        scenarios,
        (buildingsAcc, s) => ({ ...buildingsAcc, [s.building.id]: s.building }),
        {}
      );

      const newScenarios = sortScenarios(
        _.map(currentScenarios, (s: PlainProgramScenario) => {
          const selectedScenario =
            scenariosById[s.id] || ProgramScenario.toAPIScenario(s);
          const scenarioWithUpdatedBuildings = {
            ...selectedScenario,
            // A payload scenario might be bringing an updated building, we should implicitly update
            // other instances of the same building when it also appears in other scenarios
            building:
              buildings[selectedScenario.buildingId] ||
              selectedScenario.building,
          };

          return ProgramScenarioFactory.create(
            scenarioWithUpdatedBuildings
          ).toPlainObject();
        })
      ) as typeof currentScenarios;

      const scenarioIds = Object.keys(scenariosById).map((scenarioId) =>
        parseInt(scenarioId, 10)
      );

      let enhancedWorksheets: PlainWorksheet[] = yield select(
        selectWorksheets
      ) || [];

      const worksheetsByScenarioMap: {
        [key: number]: PlainWorksheet;
      } = enhancedWorksheets?.reduce((map, ws) => {
        return {
          ...map,
          [ws.scenario.id]: ws,
        };
      }, {});

      if (
        !enhancedWorksheets?.length ||
        scenarioIds.some((id) => !worksheetsByScenarioMap[id])
      ) {
        // Fetch worksheets only when they are not available
        yield put(fetchProjectWorksheets(scenarioIds));
        yield race({
          worksheets: take(FETCH_PROJECT_WORKSHEETS_SUCCESS),
          timeout: delay(3000),
        });
        enhancedWorksheets = yield select(selectWorksheets);
      }

      const updatedWorksheets = enhancedWorksheets.map((ws) => {
        const worksheetScenario = newScenarios.find(
          (s) => s.id === ws.scenario.id
        );
        if (worksheetScenario) {
          return { ...ws, scenario: worksheetScenario };
        }
        return ws;
      });

      yield put(getScenariosSuccess(newScenarios));
      yield put(buildTotalPrograms());
      const currentScenario: PlainProgramScenario = yield select(
        selectCurrentScenario
      );
      if (currentScenario && isTotalProgram(currentScenario)) {
        const { linkedScenarios } = currentScenario;
        const tpsWorksheetsEnhanced = updatedWorksheets.filter((ws) =>
          linkedScenarios.find((sc) => sc.id === ws.scenario.id)
        );

        yield put(fetchTpsWorksheetsSuccess(tpsWorksheetsEnhanced));
        yield put(actions.rebuildTotalProgramScenarioView());
      }
      yield put(fetchProjectWorksheetsSuccess(updatedWorksheets));
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* createTotalProgram(
  action: ReturnType<typeof actions.createTotalProgram>
) {
  try {
    const { totalProgram, redirect, scenarioIdsToLink } = action.payload;
    const project: IProject = yield select(currentProjectSelector);
    const formattedData: {
      name: string;
      index?: number;
      totalProgramScenario: { scenarioId: number }[];
      notes: Omit<ICategoryNote, 'id'>[];
      projectId: number;
    } = {
      index: totalProgram.index,
      totalProgramScenario: scenarioIdsToLink.map((scenario: number) => ({
        scenarioId: scenario,
      })),
      name: totalProgram.name || '',
      notes: totalProgram.notes?.map(
        (note: ICategoryNote) =>
          _.omit(note, ['id']) as Omit<ICategoryNote, 'id'>
      ),
      projectId: project.id,
    };

    const res: APIResponse<typeof createTotalProgramApi> = yield call(
      createTotalProgramApi,
      formattedData
    );
    if (res?.data) {
      const scenarios: PlainProgramScenario[] = yield select(selectScenarios);
      const totalPrograms: PlainProgramScenario[] = yield select(
        selectTotalPrograms
      );

      const linkedScenarios = scenarios.filter((_scenario) =>
        formattedData.totalProgramScenario.find(
          (s) => s.scenarioId === _scenario.id
        )
      );
      const newTotalProgram = {
        ...res.data.createTotalProgram,
        scenarios: formattedData.totalProgramScenario.map((s) => s.scenarioId),
        calculatorType: TOTAL_PROGRAM_TYPE,
      };

      const totalProgramEnhanced = ProgramScenarioFactory.create(
        newTotalProgram,
        linkedScenarios
      ).toPlainObject();

      yield put(
        createTotalProgramSuccess(newTotalProgram, totalProgramEnhanced)
      );
      yield put(
        updateNumberOfScenarios(
          (scenarios?.length || 0) + (totalPrograms?.length || 0) + 1
        )
      );

      yield put(setCurrentScenario(newTotalProgram));
      if (redirect) {
        redirect();
      }
      yield put(actions.fetchTpsWorksheets());
      yield put(
        showToastMessage({
          status: 'success',
          text: 'The scenario was successfully saved!',
        })
      );
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateTotalProgram(
  action: ReturnType<typeof actions.updateTotalProgram>
) {
  try {
    const { totalProgram, scenarioIdsToLink } = action.payload;
    const project: IProject = yield select(currentProjectSelector);
    const scenarios: PlainProgramScenario[] = yield select(selectScenarios);

    const formattedData: {
      id: number;
      index: number;
      name: string;
      totalProgramScenario: { totalProgramId: number; scenarioId: number }[];
      projectId: number;
    } = {
      id: totalProgram.id,
      index: totalProgram.index,
      name: totalProgram.name || '',
      totalProgramScenario: scenarioIdsToLink.map((scenario: number) => ({
        totalProgramId: totalProgram.id,
        scenarioId: scenario,
      })),
      projectId: project.id,
    };

    const response: APIResponse<typeof totalProgramApi.updateTotalProgram> = yield call(
      totalProgramApi.updateTotalProgram,
      formattedData
    );
    const updatedTotalProgram = response.data.totalProgram;

    const totalPrograms: PlainProgramScenario[] = yield select(
      selectTotalPrograms
    );
    const updatedTotalProgramsEnhanced = totalPrograms.slice();

    const index = totalPrograms.findIndex(
      (_totalProgram) => _totalProgram.id === updatedTotalProgram.id
    );

    if (index >= 0) {
      const linkedScenarios = getTotalProgramLinkedScenarios(
        updatedTotalProgram
      );

      const linkedScenariosList = scenarios.filter((_scenario) => {
        return linkedScenarios[_scenario.id];
      });
      const updatedTps: ITotalProgram = {
        ...totalProgram,
        ...updatedTotalProgram,
        calculatorType: TOTAL_PROGRAM_TYPE,
      };
      updatedTotalProgramsEnhanced[index] = ProgramScenarioFactory.create(
        updatedTps,
        linkedScenariosList
      );
      yield put(
        actions.totalProgramScenariosFetchSuccess(updatedTotalProgramsEnhanced)
      );
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateDepartmentScenarios(
  action: ReturnType<typeof actions.updateDepartmentScenarios>
) {
  try {
    const { rsfLoadFactor } = action.payload;

    const scenarios: PlainProgramScenario[] = yield select(selectScenarios);
    const currentScenario: PlainProgramScenario = yield select(
      selectCurrentScenario
    );
    const departmentScenarios = scenarios.filter((scenario) => {
      return (
        scenario.calculatorType === BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS
      );
    });

    if (rsfLoadFactor && !isTotalProgram(currentScenario)) {
      // if the update is for an rsfLoadFactor change, first optimistically update the
      // scenario in the redux state to improve ux
      yield put(
        actions.buildNewScenarioSuccess({
          ...ProgramScenario.toAPIScenario(currentScenario),
          rsfLoadFactor,
        })
      );
    }

    if (currentScenario && isDepartmentScenario(currentScenario)) {
      let rsfChanges = {};
      if (rsfLoadFactor !== undefined) {
        rsfChanges = { rsfLoadFactor };
      }

      const newScenarioPayload = {
        scenario: currentScenario,
        changes: { ...rsfChanges },
      };

      yield call(updateScenario, {
        type: UPDATE_SCENARIO,
        payload: newScenarioPayload,
      });
    }
    const scenariosToUpdate: Scenario[] = departmentScenarios.map(
      (scenario) => {
        let rsfChanges = {};
        if (rsfLoadFactor !== undefined) {
          rsfChanges = { rsfLoadFactor };
        }

        const scenarioToUpdate = merge(
          ProgramScenario.toAPIScenario(scenario),
          {
            ...rsfChanges,
          }
        );

        return scenarioToUpdate;
      }
    );

    const totalPrograms: PlainProgramScenario[] = yield select(
      selectTotalPrograms
    );

    const updatedTotalPrograms = totalPrograms.map((totalProgram) => {
      const linkedScenariosMap = generateLinkedScenariosIdsMap(totalProgram);

      const linkedScenarios = scenariosToUpdate.filter(
        (totalProgramScenario: Scenario) => {
          return linkedScenariosMap[totalProgramScenario.id];
        }
      );
      const newTotalProgram = TotalProgramScenario.toAPITotalProgram(
        totalProgram
      );
      return ProgramScenarioFactory.create(
        newTotalProgram,
        linkedScenarios
      ).toPlainObject();
    });

    yield put(actions.totalProgramScenariosFetchSuccess(updatedTotalPrograms));

    yield put({
      type: BULK_UPDATE_SCENARIO,
      payload: { scenarios: scenariosToUpdate },
    });
  } catch (e) {
    yield put(handleError(e));
  }
}

function* rebuildTotalProgramScenarioView() {
  try {
    const totalProgram: PlainProgramScenario = yield select(
      selectCurrentScenario
    );

    // This condition verifies that the current scenario is a total program before building and set the aggregated scenario
    if (totalProgram?.id && isTotalProgram(totalProgram)) {
      const scenarios: PlainProgramScenario[] = yield select(selectScenarios);
      const tpsWorksheets: PlainWorksheet[] = yield select(selectTpsWorksheets);
      const projectSettings: IProjectSettings = yield select(
        selectProjectSettings
      );
      const linkedScenarios = generateLinkedScenariosIdsMap(totalProgram);
      const associatedScenarios = scenarios.filter(
        (scenario) => linkedScenarios[scenario.id]
      );

      if (scenarios.length && tpsWorksheets.length) {
        const tpsWorksheetsMap: {
          [id: number]: PlainWorksheet;
        } = tpsWorksheets.reduce((worksheetMap, worksheet: PlainWorksheet) => {
          return {
            ...worksheetMap,
            [worksheet.id]: worksheet,
          };
        }, {});

        const {
          data: { totalProgramWorksheet },
        }: APIResponse<typeof worksheetApi.fetchTotalProgramWorksheet> = yield call(
          worksheetApi.fetchTotalProgramWorksheet,
          totalProgram.id
        );

        yield put(
          setCurrentScenario(
            ProgramScenarioFactory.create(
              TotalProgramScenario.toAPITotalProgram(totalProgram),
              associatedScenarios
            )
          )
        );

        const currentScenario: ProgramScenario = yield select(
          selectCurrentScenario
        );

        const childWorksheetSpaces: IChildWorksheetSpaceType[] = yield select(
          selectAllChildWorksheetSpaces
        );
        const projectSpaceTypes: IProjectSpaceType[] = yield select(
          projectSpaceTypesSelector
        );
        const projectSpaceTypesCategories: IProjectSpaceTypeCategory[] = yield select(
          selectProjectSpaceTypeCategories
        );

        // TODO: Build total program worksheet from the data returned by the fetchTotalProgramWorksheet query
        const apiMissingWorksheets: APIResponse<
          typeof worksheetApi.fetchWorksheet
        >[] = yield all(
          uniq(
            totalProgramWorksheet.rows
              .filter(
                (row: IWorksheetRow) => !tpsWorksheetsMap[row.worksheetId]
              )
              .map((row: IWorksheetRow) => row.scenarioId)
          ).map((scenarioId: number) => {
            return call(worksheetApi.fetchWorksheet, scenarioId);
          })
        );

        const missingWorksheets = apiMissingWorksheets.map(
          (response: APIResponse<typeof worksheetApi.fetchWorksheet>) =>
            response.data.worksheet
        );

        missingWorksheets.forEach((worksheet: IWorksheet) => {
          tpsWorksheetsMap[worksheet.id] = new Worksheet(
            worksheet,
            scenarios.find((s) => s.id === worksheet.scenarioId),
            projectSettings
          ).toPlainObject();
        });

        const rows = hydrateWorksheetRows({
          projectSpaceTypes,
          rows: totalProgramWorksheet.rows.map((row: IWorksheetRow) => {
            return {
              ...row,
              department: tpsWorksheetsMap[row.worksheetId].scenario.department,
            };
          }),
          childWorksheetSpaces,
          projectSpaceTypesCategories,
        }).map((row) => {
          if (isStorageSupportSpace(row.spaceType) && !row.removed) {
            const { scenario } = tpsWorksheetsMap[row.worksheetId];

            return new WorksheetStorageSpaceRow(
              row,
              scenario,
              projectSettings,
              row.worksheetId
            );
          }
          return new WorksheetRow(row, row.worksheetId);
        });

        const worksheet = new TotalProgramWorksheet(
          tpsWorksheets,
          currentScenario,
          projectSettings,
          rows
        ).toPlainObject();
        yield put(populateWorksheet(worksheet));
      }
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* bulkUpdateTotalPrograms(
  action: ReturnType<typeof actions.bulkUpdateTotalPrograms>
) {
  try {
    const { totalPrograms: apiTotalPrograms } = action.payload;
    if (apiTotalPrograms && apiTotalPrograms.length) {
      const updates = _.map(apiTotalPrograms, (tp: ITotalProgram) => {
        return call(
          totalProgramApi.updateTotalProgram,
          _.pick(tp, [
            'id',
            'name',
            'index',
            'subtotalProgramMultiplier',
            'planningFactor',
            'usf',
          ])
        );
      });
      yield all(updates);
      const updatedTotalPrograms = _.map(
        apiTotalPrograms,
        (t: ITotalProgram) =>
          ({
            ...t,
            calculatorType: TOTAL_PROGRAM_TYPE,
          } as ITotalProgram)
      );
      const totalPrograms: PlainProgramScenario[] = yield select(
        selectTotalPrograms
      );
      const updatedTotalProgramsEnhanced = updatedTotalPrograms.map((tps) =>
        TotalProgramScenario.updateTotalProgramProps(
          totalPrograms.find((tpe) => tpe.id === tps.id),
          tps
        )
      );
      yield put(
        totalProgramScenariosFetchSuccess(updatedTotalProgramsEnhanced)
      );
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateChildWorksheetSpaceType(
  action: ReturnType<typeof actions.updateChildWorksheetSpaceType>
) {
  try {
    const { childWorksheetSpaceType } = action.payload;
    const { unitsHigh } = action.payload;

    if (childWorksheetSpaceType) {
      const response: APIResponse<typeof updateChildWorksheetSpaceTypeApi> = yield call(
        updateChildWorksheetSpaceTypeApi,
        {
          name: childWorksheetSpaceType.name,
          clientFacingName: childWorksheetSpaceType.clientFacingName,
          id: childWorksheetSpaceType.id,
          unitsHigh: unitsHigh || null,
        }
      );

      if (response.data) {
        const {
          updateChildWorksheetSpaceType: newChildWorksheetSpaceType,
        } = response.data;
        yield put(createChildWorksheetSpaceSuccess(newChildWorksheetSpaceType));
        const worksheet: PlainWorksheet = yield select(selectWorksheet);
        if (worksheet) {
          if (isTotalProgram(worksheet.scenario)) {
            yield put(actions.rebuildTotalProgramScenarioView());
            return;
          }
          yield put(
            populateWorksheetFromRequestAction(
              Worksheet.toAPIWorksheet(worksheet)
            )
          );
        }
      }
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* fetchAllChildWorksheetSpaces() {
  try {
    const currentProject: IProject = yield select(currentProjectSelector);
    yield put(
      actions.setChildWorksheetSpacesRequestStatus(REQUEST_STATUS.QUERYING)
    );

    if (currentProject) {
      const response: APIResponse<typeof fetchAllChildWorksheetSpacesApi> = yield call(
        fetchAllChildWorksheetSpacesApi,
        currentProject.id
      );
      const isFetchingSpaceTypes: boolean = yield select(
        selectIsFetchingSpaceTypes
      );
      if (isFetchingSpaceTypes) yield take(FETCH_PROJECT_SPACE_TYPES_SUCCESS);
      const projectSpaceTypes: IProjectSpaceType[] = yield select(
        projectSpaceTypesSelector
      );

      const {
        data: { childWorksheetSpaceTypes },
      } = response;

      let hydratedSpaces: IChildWorksheetSpaceType[] = [];
      if (childWorksheetSpaceTypes) {
        hydratedSpaces = _(childWorksheetSpaceTypes)
          .map((child: Record<string, any>) => {
            const parentProjectSpaceType = _.find(
              projectSpaceTypes,
              (s) => s?.spaceType?.id === child?.spaceTypeId
            );

            if (parentProjectSpaceType) {
              const parentSpace = {
                ...parentProjectSpaceType.spaceType,
                spaceTypeCategory: parentProjectSpaceType.category
                  .defaultSpaceTypeCategory as ISpaceTypeCategory,
              };

              return {
                id: child.id,
                name: child.name,
                clientFacingName: child.clientFacingName,
                spaceTypeId: child.spaceTypeId,
                projectId: child.projectId,
                spaceType: parentSpace?.spaceType,
                parent: parentSpace,
                icon: parentSpace.icon,
                unitsHigh: child.unitsHigh,
                rank: child.rank,
              };
            }
            return null;
          })
          .compact()
          .value();

        yield put(fetchAllChildWorksheetSpacesSuccess(hydratedSpaces));
        const worksheet: PlainWorksheet = yield select(selectWorksheet);
        const scenario: PlainProgramScenario = yield select(
          selectCurrentScenario
        );
        if (
          worksheet &&
          scenario &&
          scenario?.calculatorType !== TOTAL_PROGRAM_TYPE
        ) {
          yield put(
            populateWorksheetFromRequestAction(
              Worksheet.toAPIWorksheet(worksheet)
            )
          );
        }
      }
    }
  } catch (e) {
    yield put(
      actions.setChildWorksheetSpacesRequestStatus(REQUEST_STATUS.NONE)
    );
    yield put(handleError(e));
  }
}

function* updateTotalProgramLinkedScenarios(
  action: ReturnType<typeof actions.updateLinkedScenarios>
) {
  try {
    const scenarios: PlainProgramScenario[] = yield select(selectScenarios);

    const { totalProgram, changes } = action.payload;

    const totalProgramScenarios: {
      [key: string]: boolean;
    } = getTotalProgramLinkedScenarios(totalProgram);

    const scenariosToUpdate = scenarios
      .slice()
      .filter((scenario) => totalProgramScenarios[scenario.id])
      .map((scenario) => {
        return merge(ProgramScenario.toAPIScenario(scenario), {
          subtotalBuildingMultiplier: isNumber(
            changes.subtotalBuildingMultiplier
          )
            ? changes.subtotalBuildingMultiplier
            : scenario.subtotalMultiplierForCirculation,
          subtotalProgramMultiplier: isNumber(changes.subtotalProgramMultiplier)
            ? changes.subtotalProgramMultiplier
            : scenario.planningFactorPercentage,
          usf: changes.usf ? changes.usf : scenario.totalAvailableUSF,
        });
      });

    yield call(bulkUpdateScenarios, {
      type: BULK_UPDATE_SCENARIO,
      payload: { scenarios: scenariosToUpdate },
    });
  } catch (e) {
    yield put(handleError(e));
  }
}

// this function handles hydrating all worksheet row values when any changes are made to worksheet/worksheet rows
function* populateWorksheetFromRequest(
  action: ReturnType<typeof actions.populateWorksheetFromRequest>
) {
  try {
    const { worksheet: apiWorksheet } = action.payload;
    const oldWorksheet: PlainWorksheet = yield select(selectWorksheet);
    const projectSpaceTypes: IProjectSpaceType[] = yield select(
      projectSpaceTypesSelector
    );
    const childWorksheetSpaces: IChildWorksheetSpaceType[] = yield select(
      selectAllChildWorksheetSpaces
    );

    const projectSpaceTypesCategories: IProjectSpaceTypeCategory[] = yield select(
      selectProjectSpaceTypeCategories
    );

    const rows = hydrateWorksheetRows({
      projectSpaceTypes,
      rows: isWorksheetAPIObject(apiWorksheet)
        ? apiWorksheet.worksheetRows
        : apiWorksheet.rows,
      childWorksheetSpaces,
      projectSpaceTypesCategories,
    });

    const scenarios: PlainProgramScenario[] = yield select(selectScenarios);
    const currentScenario: PlainProgramScenario = yield select(
      selectCurrentScenario
    );

    let scenario: PlainProgramScenario = scenarios.find(
      (s: PlainProgramScenario) => s.id === apiWorksheet.scenarioId
    );

    if (!scenario && currentScenario) {
      // When the scenario doesn't exist in the list, it's a new scenario
      scenario = currentScenario;
    }

    const totalWorkSeats = scenario?.workSeats ?? 0;

    // Temporary solution to correct ratios stored in the database
    const rowsWithRecalculatedRatio = rows.map((row) => {
      const correctedRatio = getRowRatio(
        row,
        scenario?.areStoredWorkSeatsOutdated
          ? scenario.storedWorkSeats
          : totalWorkSeats
      );
      return { ...row, ratio: correctedRatio };
    });

    const newWorksheet = {
      notes: oldWorksheet?.notes, // this guarantees notes are carried over
      ...apiWorksheet,
      rows: rowsWithRecalculatedRatio,
    };

    if (isWorksheetAPIObject(newWorksheet)) delete newWorksheet.worksheetRows;

    const projectSettings: IProjectSettings = yield select(
      selectProjectSettings
    );
    let worksheet: Worksheet;
    if (scenario) {
      worksheet = new Worksheet(newWorksheet, scenario, projectSettings);
    }

    const worksheets: PlainWorksheet[] = yield select(selectWorksheets);

    const worksheetIndex = worksheets.findIndex(
      (ws) => ws.id === newWorksheet.id
    );

    if (worksheetIndex > -1) {
      const worksheetScenario = worksheets[worksheetIndex].scenario;
      worksheets.splice(
        worksheetIndex,
        1,
        new Worksheet(newWorksheet, worksheetScenario, projectSettings)
      );
    }

    yield put(populateWorksheet(worksheet?.toPlainObject()));
  } catch (e) {
    yield put(handleError(e));
  }
}

export default function* saga() {
  yield all([
    takeLatest(BUILD_NEW_SCENARIO, buildNewScenario),
    takeLatest(UPDATE_SCENARIO, updateScenario),
    takeLatest(SAVE_SCENARIO, saveScenario),
    takeLatest(GET_PROJECT_SCENARIOS, getProjectScenarios),
    takeLatest(GET_SCENARIO, getScenario),
    takeLatest(GET_TOTAL_PROGRAM, getTotalProgram),
    takeLatest(SAVE_UPDATING_SCENARIO, saveUpdatingScenario),
    takeLatest(SAVE_WORKSHEET, saveWorksheet),
    takeLatest(FETCH_CURRENT_WORKSHEET, fetchCurrentWorksheet),
    takeLatest(DELETE_SCENARIO, deleteScenario),
    takeLatest(POST_WORKSHEET_ROW, postWorksheetRow),
    takeEvery(CREATE_WORKSHEET_NOTE, createWorksheetNote),
    takeLatest(
      [
        SET_SUBTOTAL_PROGRAM_MULTIPLIER,
        SET_RSF_LOAD_FACTOR,
        SET_SUBTOTAL_BUILDING_MULTIPLIER,
        SET_PLANNING_FACTOR,
        SET_TOTAL_PROGRAM_AVAILABLE_USF,
      ],
      saveUpdatingScenario
    ),
    takeLatest(
      scenarioActions.SAVE_SCENARIO_WORK_SEATS_ROUNDING_SETTING,
      saveScenarioWorkSeatsRoundingSettingCall
    ),
    takeLatest(PREPARE_SCENARIO_FOR_UPDATE, prepareScenarioForUpdate),
    takeLatest(BULK_UPDATE_SCENARIO, bulkUpdateScenarios),
    takeLatest(UPDATE_DEPARTMENT_SCENARIOS, updateDepartmentScenarios),
    takeLatest(CREATE_TOTAL_PROGRAM, createTotalProgram),
    takeLatest(UPDATE_TOTAL_PROGRAM, updateTotalProgram),
    takeEvery(CREATE_TOTAL_PROGRAM_NOTE, createTotalProgramNote),
    takeLatest(FETCH_TPS_WORKSHEETS, fetchTpsWorksheetsCall),
    takeLatest(FETCH_PROJECT_WORKSHEETS, fetchProjectWorksheetsCall),
    takeLatest(
      REBUILD_TOTAL_PROGRAM_SCENARIO_VIEW,
      rebuildTotalProgramScenarioView
    ),
    takeLatest(BULK_UPDATE_TOTAL_PROGRAM, bulkUpdateTotalPrograms),
    takeLatest(
      UPDATE_CHILD_WORKSHEET_SPACE_TYPE,
      updateChildWorksheetSpaceType
    ),
    takeLatest(FETCH_ALL_CHILD_WORKSHEET_SPACES, fetchAllChildWorksheetSpaces),
    takeLatest(UPDATE_LINKED_SCENARIOS, updateTotalProgramLinkedScenarios),
    takeLatest(POPULATE_WORKSHEET_FROM_REQUEST, populateWorksheetFromRequest),
    takeLatest(CLONE_SCENARIO, cloneScenario),
    takeLatest(BUILD_TOTAL_PROGRAMS, buildTotalProgramsCall),
  ]);
}
